import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  message,
  Space,
  Typography,
  Divider,
  Tooltip
} from 'antd'
import { LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons'
import api from '../../utils/api'
import styled from 'styled-components'
import Hint from '../../components/common/Hint'

const { Title, Text } = Typography
const { Option } = Select

const PageContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
`

const ChangeBusinessPasswordPage = () => {
  const [form] = Form.useForm()
  const [businessUsers, setBusinessUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [queryingPassword, setQueryingPassword] = useState(false)
  const [plainPasswordInfo, setPlainPasswordInfo] = useState(null)
  const [showPlainPassword, setShowPlainPassword] = useState(false)

  useEffect(() => {
    fetchBusinessUsers()
  }, [])

  const fetchBusinessUsers = async () => {
    try {
      setLoadingUsers(true)
      const response = await api.get(
        `/api/admin/business-users?page=1&limit=1000`
      )
      const users = response.data.data || []
      setBusinessUsers(users)
    } catch (err) {
      console.error('獲取店家用戶列表失敗:', err)
      message.error('獲取店家用戶列表失敗')
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleChangePassword = async (values) => {
    try {
      setChangingPassword(true)
      await api.put(
        `/api/auth/admin/business-user/${values.businessUserId}/password`,
        {
          newPassword: values.newPassword
        }
      )
      message.success('密碼修改成功')
      form.resetFields()
      setPlainPasswordInfo(null)
    } catch (err) {
      console.error('修改密碼失敗:', err)
      message.error(err.response?.data?.message || '修改密碼失敗，請稍後再試')
    } finally {
      setChangingPassword(false)
    }
  }

  const handleQueryPlainPassword = async () => {
    const userId = form.getFieldValue('businessUserId')
    if (!userId) {
      message.warning('請先選擇店家用戶')
      return
    }
    try {
      setQueryingPassword(true)
      setPlainPasswordInfo(null)
      setShowPlainPassword(false)
      const res = await api.get(
        `/api/auth/admin/business-user/${userId}/plain-password`
      )
      setPlainPasswordInfo(res.data.data)
    } catch (err) {
      console.error('查詢明碼失敗:', err)
      message.error(err.response?.data?.error || '查詢失敗')
    } finally {
      setQueryingPassword(false)
    }
  }

  return (
    <PageContainer>
      <StyledCard
        title={
          <Space>
            <LockOutlined />
            <Title level={4} style={{ margin: 0 }}>
              修改店家密碼
            </Title>
          </Space>
        }
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleChangePassword}
          size='large'
        >
          <Form.Item
            name='businessUserId'
            label='選擇店家用戶'
            rules={[{ required: true, message: '請選擇店家用戶' }]}
          >
            <Select
              placeholder='請選擇要修改密碼的店家用戶'
              loading={loadingUsers}
              showSearch
              filterOption={(input, option) => {
                const text = option?.children
                if (!text) return false
                const textStr = typeof text === 'string' ? text : String(text)
                return textStr.toLowerCase().includes(input.toLowerCase())
              }}
            >
              {businessUsers.map((user) => {
                const displayText = `${user.businessName} - ${user.name} (${
                  user.email || user.username
                })`
                return (
                  <Option key={user._id} value={user._id}>
                    {displayText}
                  </Option>
                )
              })}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              icon={<EyeOutlined />}
              onClick={handleQueryPlainPassword}
              loading={queryingPassword}
              style={{ marginBottom: 4 }}
            >
              查詢目前密碼
            </Button>
            {plainPasswordInfo && (
              <Hint
                style={{ marginTop: 12 }}
                tone='info'
                title={
                  <Space>
                    <Text strong>帳號：</Text>
                    <Text>{plainPasswordInfo.username}</Text>
                    <Divider type='vertical' />
                    <Text strong>目前密碼：</Text>
                    {showPlainPassword ? (
                      <Text code copyable>
                        {plainPasswordInfo.plainPassword || '（尚無記錄，可能是系統建立前的舊帳號）'}
                      </Text>
                    ) : (
                      <Text>••••••</Text>
                    )}
                    <Tooltip title={showPlainPassword ? '隱藏' : '顯示'}>
                      <Button
                        type='text'
                        size='small'
                        icon={showPlainPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowPlainPassword((v) => !v)}
                      />
                    </Tooltip>
                  </Space>
                }
              />
            )}
          </Form.Item>

          <Divider />

          <Form.Item
            name='newPassword'
            label='新密碼'
            rules={[
              { required: true, message: '請輸入新密碼' },
              { min: 6, message: '密碼長度至少為 6 個字符' }
            ]}
          >
            <Input.Password
              placeholder='請輸入新密碼（至少 6 個字符）'
              prefix={<LockOutlined />}
            />
          </Form.Item>

          <Form.Item
            name='confirmPassword'
            label='確認新密碼'
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '請確認新密碼' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('兩次輸入的密碼不一致'))
                }
              })
            ]}
          >
            <Input.Password
              placeholder='請再次輸入新密碼'
              prefix={<LockOutlined />}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              loading={changingPassword}
              icon={<LockOutlined />}
              block
              size='large'
            >
              修改密碼
            </Button>
          </Form.Item>
        </Form>
      </StyledCard>
    </PageContainer>
  )
}

export default ChangeBusinessPasswordPage
